import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RegionToggle } from "@/components/RegionToggle";
import { getTerms } from "@/lib/terms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, UserCog, Save, Landmark, ExternalLink, ShieldCheck, CheckCircle2, Loader2, Lock, DatabaseZap, FileDown, PenLine, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Field } from "@/pages/Vehicles";
import { FileUpload } from "@/components/FileUpload";

const LICENCE_TYPES = ["Standard National", "Standard International", "Restricted"];

const Card = ({ icon: Icon, title, children }) => (
  <div className="bg-white border border-slate-200 rounded-md p-6 animate-in-up">
    <div className="flex items-center gap-2 mb-5">
      <Icon size={18} className="text-slate-900" />
      <h3 className="font-heading font-bold text-lg tracking-tight">{title}</h3>
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const empty = {
  company_name: "", company_number: "", operator_licence_number: "", licence_type: "Standard National",
  address: "", authorised_vehicles: 0, authorised_trailers: 0,
  tm_name: "", tm_cpc_number: "", tm_email: "", tm_phone: "", notes: "", logo_file_id: "", letterhead_file_id: "", signature_file_id: "", signoff_role: "",
  vat_number: "", eori_number: "", gmr_reference: "", bank_sort_code: "", bank_account_number: "", bank_swift: "", bank_iban: "",
  website: "", email: "",
};

export default function Operator() {
  const { user } = useAuth();
  const terms = getTerms(user?.region);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [owner, setOwner] = useState({ owner_email: "", is_owner: false, your_email: "" });
  const [newOwner, setNewOwner] = useState("");
  const [ownerBusy, setOwnerBusy] = useState(false);
  const [isoBusy, setIsoBusy] = useState(false);
  const [iso, setIso] = useState(null);

  const runIsolationCheck = async () => {
    setIsoBusy(true);
    try {
      const r = await api.get("/security/isolation-check");
      setIso(r.data);
      if (r.data.ok) toast.success("Data isolation verified across all collections");
      else toast.error("Isolation check found an issue — please contact support");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not run isolation check"); }
    finally { setIsoBusy(false); }
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadIsoPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get("/security/isolation-check/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `data-isolation-audit-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Audit PDF downloaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate PDF"); }
    finally { setPdfBusy(false); }
  };

  useEffect(() => {
    if (user?.role !== "manager") return;
    api.get("/company/owner").then((r) => { setOwner(r.data); setNewOwner(r.data.owner_email || ""); }).catch(() => {});
  }, [user]);

  const saveOwner = async () => {
    setOwnerBusy(true);
    try {
      const r = await api.put("/company/owner", { owner_email: newOwner });
      setOwner((o) => ({ ...o, owner_email: r.data.owner_email }));
      toast.success("Company owner / super user updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update owner"); }
    finally { setOwnerBusy(false); }
  };

  useEffect(() => {
    api.get("/operator").then((r) => setForm({ ...empty, ...r.data }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [signatories, setSignatories] = useState([]);
  const [sigForm, setSigForm] = useState({ id: null, name: "", role: "", signature_file_id: "" });
  const [sigBusy, setSigBusy] = useState(false);
  const loadSignatories = () => api.get("/signatories").then((r) => setSignatories(r.data || [])).catch(() => {});
  useEffect(() => { loadSignatories(); }, []);
  const resetSig = () => setSigForm({ id: null, name: "", role: "", signature_file_id: "" });
  const saveSignatory = async () => {
    if (!sigForm.name.trim()) { toast.error("Enter a name"); return; }
    setSigBusy(true);
    try {
      const body = { name: sigForm.name.trim(), role: sigForm.role.trim(), signature_file_id: sigForm.signature_file_id || "" };
      if (sigForm.id) await api.put(`/signatories/${sigForm.id}`, body);
      else await api.post("/signatories", body);
      toast.success(sigForm.id ? "Signatory updated" : "Signatory added");
      resetSig(); loadSignatories();
    } catch { toast.error("Could not save signatory"); }
    finally { setSigBusy(false); }
  };
  const deleteSignatory = async (id) => {
    if (!window.confirm("Delete this signatory?")) return;
    try { await api.delete(`/signatories/${id}`); toast.success("Signatory deleted"); if (sigForm.id === id) resetSig(); loadSignatories(); }
    catch { toast.error("Could not delete"); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/operator", { ...form, authorised_vehicles: Number(form.authorised_vehicles) || 0, authorised_trailers: Number(form.authorised_trailers) || 0 });
      toast.success("Operator details saved");
    } catch { toast.error("Could not save details"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="operator-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Compliance · {terms.authority}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mt-1">Operator Details</h1>
          <p className="text-slate-500 text-sm mt-1">Company, {terms.operatorLicence} & Transport Manager details</p>
        </div>
        <div className="flex items-center gap-3">
          <RegionToggle />
          <Button data-testid="save-operator-button" onClick={save} disabled={busy} className="bg-black hover:bg-slate-800 rounded-md gap-2"><Save size={16} /> {busy ? "Saving…" : "Save Details"}</Button>
        </div>
      </div>

      {user?.role === "manager" && (
      <div className="mb-6" data-testid="company-owner-card">
        <Card icon={ShieldCheck} title="Company Owner / Super User">
          <p className="text-sm text-slate-500">The owner has full access &amp; control of this company account. You can transfer ownership to another email address at any time.</p>
          <Field label="Current owner / super user">
            <Input data-testid="owner-current-email" value={owner.owner_email} disabled className="bg-slate-50" />
          </Field>
          {owner.is_owner ? (
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1">
                <Field label="Transfer ownership to (email)">
                  <Input data-testid="owner-new-email" type="email" value={newOwner} onChange={(e) => setNewOwner(e.target.value)} placeholder="new.owner@company.co.uk" />
                </Field>
              </div>
              <Button data-testid="owner-transfer-button" onClick={saveOwner} disabled={ownerBusy} className="bg-slate-900 hover:bg-slate-800 rounded-md gap-2"><Save size={15} /> {ownerBusy ? "Saving…" : "Transfer ownership"}</Button>
            </div>
          ) : (
            <p data-testid="owner-locked-note" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">Only the current owner / super user can change this.</p>
          )}
        </Card>
      </div>
      )}

      {user?.role === "manager" && (
      <div className="mb-6" data-testid="isolation-check-card">
        <Card icon={DatabaseZap} title="Data Isolation Self-Check">
          <p className="text-sm text-slate-500">Run a live check that confirms your company's data is fully isolated at the database level — your account can only ever see your own records. Useful evidence for auditors.</p>
          <div className="flex flex-wrap gap-3 mt-1">
            <Button data-testid="run-isolation-check" onClick={runIsolationCheck} disabled={isoBusy} className="bg-slate-900 hover:bg-slate-800 rounded-md gap-2">
              {isoBusy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} {isoBusy ? "Running live check…" : "Run isolation self-check"}
            </Button>
            <Button data-testid="download-isolation-pdf" variant="outline" onClick={downloadIsoPdf} disabled={pdfBusy} className="rounded-md gap-2">
              {pdfBusy ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} Download audit PDF
            </Button>
          </div>

          {iso && (
            <div data-testid="isolation-report" className="mt-5">
              <div className={`rounded-md border-2 px-5 py-4 ${iso.ok ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
                <p className={`flex items-center gap-2 font-heading font-black text-lg ${iso.ok ? "text-emerald-700" : "text-red-700"}`}>
                  {iso.ok ? <CheckCircle2 size={22} /> : <Lock size={22} />}
                  <span data-testid="isolation-headline">{iso.ok ? "DATA ISOLATION OK ACROSS ALL COLLECTIONS" : "ISOLATION ISSUE DETECTED"}</span>
                </p>
                <p className="text-xs text-slate-600 mt-1">Checked {new Date(iso.checked_at).toLocaleString()} · Account {iso.company_id_masked} · {iso.collections_checked} collections</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                  {[
                    ["Your records", iso.your_records_total],
                    ["Other companies' records (excluded)", iso.other_companies_records_total],
                    ["Other companies in system", iso.other_companies_in_system],
                    ["Foreign records in your scope", iso.foreign_records_in_your_scope],
                  ].map(([label, val], i) => (
                    <div key={i} className="bg-white rounded-md border border-slate-200 px-3 py-2">
                      <p className={`text-xl font-black ${label.startsWith("Foreign") ? (val === 0 ? "text-emerald-600" : "text-red-600") : "text-slate-900"}`}>{val}</p>
                      <p className="text-[11px] text-slate-500 leading-tight mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 border border-slate-200 rounded-md overflow-hidden">
                <div className="max-h-80 overflow-y-auto overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                        <th className="px-3 py-2 font-semibold">Collection</th>
                        <th className="px-3 py-2 font-semibold text-right">Your records</th>
                        <th className="px-3 py-2 font-semibold text-right">Others (excluded)</th>
                        <th className="px-3 py-2 font-semibold text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {iso.collections.map((c) => (
                        <tr key={c.collection} data-testid={`iso-row-${c.collection}`} className="text-slate-700">
                          <td className="px-3 py-1.5 font-mono text-xs">{c.collection}</td>
                          <td className="px-3 py-1.5 text-right">{c.your_records}</td>
                          <td className="px-3 py-1.5 text-right text-slate-400">{c.other_companies_records}</td>
                          <td className="px-3 py-1.5 text-right">
                            {c.status === "isolated"
                              ? <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold"><CheckCircle2 size={13} /> Isolated</span>
                              : <span className="inline-flex items-center gap-1 text-red-600 font-semibold"><Lock size={13} /> LEAK</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
      )}

      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card icon={Building2} title="Company & Licence">
          <Field label="Company Name"><Input data-testid="op-company-name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="HaulCheck Logistics Ltd" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Company Number"><Input data-testid="op-company-number" value={form.company_number} onChange={(e) => setForm({ ...form, company_number: e.target.value })} placeholder="12345678" /></Field>
            <Field label={`${terms.operatorLicence} No.`}><Input data-testid="op-licence-number" value={form.operator_licence_number} onChange={(e) => setForm({ ...form, operator_licence_number: e.target.value })} placeholder="OB1234567" /></Field>
          </div>
          <Field label="Licence Type">
            <Select value={form.licence_type} onValueChange={(v) => setForm({ ...form, licence_type: v })}>
              <SelectTrigger data-testid="op-licence-type"><SelectValue /></SelectTrigger>
              <SelectContent>{LICENCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="rounded-md border border-blue-200 bg-blue-50/50 p-3">
            <p className="text-xs font-semibold text-blue-900 mb-2">Scan your operator's licence — AI fills the number & type</p>
            <FileUpload testid="op-licence-ai" label="Upload operator's licence (photo or PDF)" extractType="olicence"
              onExtract={(f) => setForm((p) => ({ ...p, operator_licence_number: f.operator_licence_number ?? p.operator_licence_number, licence_type: f.licence_type ?? p.licence_type }))}
              attachments={[]} onChange={() => {}} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Authorised Vehicles"><Input data-testid="op-auth-vehicles" type="number" min="0" value={form.authorised_vehicles} onChange={(e) => setForm({ ...form, authorised_vehicles: e.target.value })} /></Field>
            <Field label="Authorised Trailers"><Input data-testid="op-auth-trailers" type="number" min="0" value={form.authorised_trailers} onChange={(e) => setForm({ ...form, authorised_trailers: e.target.value })} /></Field>
          </div>
          <Field label="Operating Centre Address"><Textarea data-testid="op-address" rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Full operating centre address" /></Field>
          <Field label="Company Logo (small mark, top of generated documents)">
            <FileUpload testid="op-logo" accept="image/*" label="Upload logo (PNG / JPG)"
              attachments={form.logo_file_id ? [{ file_id: form.logo_file_id, filename: "logo", content_type: "image/png" }] : []}
              onChange={(a) => setForm({ ...form, logo_file_id: a.length ? a[a.length - 1].file_id : "" })} />
          </Field>
          <Field label="Saved letterhead (used automatically on every generated letter — you can still override per letter)">
            <FileUpload testid="op-letterhead" accept="image/*" label="Upload a letterhead image (prints across the top)"
              attachments={form.letterhead_file_id ? [{ file_id: form.letterhead_file_id, filename: "letterhead", content_type: "image/png" }] : []}
              onChange={(a) => setForm({ ...form, letterhead_file_id: a.length ? a[a.length - 1].file_id : "" })} />
          </Field>
          <Field label="Sign-off role (shown under the name on letters)">
            <Input data-testid="op-signoff-role" value={form.signoff_role} onChange={(e) => setForm({ ...form, signoff_role: e.target.value })} placeholder="e.g. Transport Manager" />
          </Field>
          <Field label="Signature image (default sign-off — prints above the name on letters)">
            <FileUpload testid="op-signature" accept="image/*" label="Upload a signature image (PNG with transparent background works best)"
              attachments={form.signature_file_id ? [{ file_id: form.signature_file_id, filename: "signature", content_type: "image/png" }] : []}
              onChange={(a) => setForm({ ...form, signature_file_id: a.length ? a[a.length - 1].file_id : "" })} />
          </Field>
        </Card>

        <Card icon={PenLine} title="Signatories">
          <p className="text-sm text-slate-500 -mt-2 mb-1">Save more than one signatory (e.g. Transport Manager and a Director) and pick who signs each letter in the Generate Document dialog. Your default sign-off above is always available too.</p>
          {signatories.length > 0 && (
            <div className="space-y-2" data-testid="signatory-list">
              {signatories.map((s) => (
                <div key={s.id} data-testid="signatory-row" className="flex items-center justify-between gap-3 rounded-md border border-slate-200 p-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {s.signature_file_id
                      ? <img src={`${API}/files/${s.signature_file_id}?auth=${localStorage.getItem("token") || ""}`} alt="" className="h-8 w-16 object-contain shrink-0" />
                      : <span className="h-8 w-16 shrink-0 rounded bg-slate-100 text-[10px] text-slate-400 flex items-center justify-center">no image</span>}
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{s.name}</p>
                      {s.role && <p className="text-xs text-slate-500 truncate">{s.role}</p>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button type="button" data-testid="signatory-edit" onClick={() => setSigForm({ id: s.id, name: s.name, role: s.role || "", signature_file_id: s.signature_file_id || "" })} className="p-2 rounded-md text-slate-500 hover:bg-slate-100"><Pencil size={15} /></button>
                    <button type="button" data-testid="signatory-delete" onClick={() => deleteSignatory(s.id)} className="p-2 rounded-md text-red-500 hover:bg-red-50"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="rounded-md border border-dashed border-slate-300 p-4 space-y-3" data-testid="signatory-form">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{sigForm.id ? "Edit signatory" : "Add a signatory"}</p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name"><Input data-testid="signatory-name" value={sigForm.name} onChange={(e) => setSigForm({ ...sigForm, name: e.target.value })} placeholder="e.g. Jane Director" /></Field>
              <Field label="Role"><Input data-testid="signatory-role" value={sigForm.role} onChange={(e) => setSigForm({ ...sigForm, role: e.target.value })} placeholder="e.g. Managing Director" /></Field>
            </div>
            <Field label="Signature image">
              <FileUpload testid="signatory-signature" accept="image/*" label="Upload a signature image"
                attachments={sigForm.signature_file_id ? [{ file_id: sigForm.signature_file_id, filename: "signature", content_type: "image/png" }] : []}
                onChange={(a) => setSigForm({ ...sigForm, signature_file_id: a.length ? a[a.length - 1].file_id : "" })} />
            </Field>
            <div className="flex gap-2">
              <Button type="button" data-testid="signatory-save" onClick={saveSignatory} disabled={sigBusy} className="bg-slate-900 hover:bg-slate-800 gap-2">
                {sigBusy ? <Loader2 size={15} className="animate-spin" /> : (sigForm.id ? <Pencil size={15} /> : <Plus size={15} />)} {sigForm.id ? "Update signatory" : "Add signatory"}
              </Button>
              {sigForm.id && <Button type="button" variant="outline" data-testid="signatory-cancel" onClick={resetSig}>Cancel</Button>}
            </div>
          </div>
        </Card>

        <Card icon={UserCog} title="Transport Manager">
          <Field label="TM Name"><Input data-testid="op-tm-name" value={form.tm_name} onChange={(e) => setForm({ ...form, tm_name: e.target.value })} placeholder="Transport Manager full name" /></Field>
          <Field label="TM CPC Number"><Input data-testid="op-tm-cpc" value={form.tm_cpc_number} onChange={(e) => setForm({ ...form, tm_cpc_number: e.target.value })} placeholder="Certificate of Professional Competence no." /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="TM Email"><Input data-testid="op-tm-email" type="email" value={form.tm_email} onChange={(e) => setForm({ ...form, tm_email: e.target.value })} placeholder="tm@company.com" /></Field>
            <Field label="TM Phone"><Input data-testid="op-tm-phone" value={form.tm_phone} onChange={(e) => setForm({ ...form, tm_phone: e.target.value })} placeholder="07…" /></Field>
          </div>
          <Field label="Notes"><Textarea data-testid="op-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional operator notes, undertakings, conditions…" /></Field>
        </Card>

        <div className="lg:col-span-2">
          <Card icon={Landmark} title="Financial & Contact">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="VAT Number"><Input data-testid="op-vat" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder="GB123456789" /></Field>
              <Field label="EORI Number"><Input data-testid="op-eori" value={form.eori_number} onChange={(e) => setForm({ ...form, eori_number: e.target.value })} placeholder="GB123456789000" /></Field>
              <div className="sm:col-span-2">
                <Field label="GMR / HMRC reference (GVMS)">
                  <div className="flex gap-2">
                    <Input data-testid="op-gmr" value={form.gmr_reference} onChange={(e) => setForm({ ...form, gmr_reference: e.target.value })} placeholder="e.g. GMRA0001ABCD — your Goods Movement Reference" className="flex-1" />
                    <a data-testid="op-open-hmrc" href="https://www.gov.uk/log-in-hmrc-goods-vehicle-movement-service" target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 shrink-0 rounded-md border border-slate-300 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
                      Open HMRC <ExternalLink size={14} />
                    </a>
                  </div>
                </Field>
                <p className="text-xs text-slate-400 mt-1">Saved here and pre-filled onto movement records. Use "Open HMRC" to complete the government step on the official GVMS service.</p>
              </div>
              <Field label="Website"><Input data-testid="op-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="www.company.co.uk" /></Field>
              <Field label="Company Email"><Input data-testid="op-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="info@company.co.uk" /></Field>
            </div>
            <div className="pt-2">
              <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-3">Bank account details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Sort Code"><Input data-testid="op-bank-sort" value={form.bank_sort_code} onChange={(e) => setForm({ ...form, bank_sort_code: e.target.value })} placeholder="12-34-56" /></Field>
                <Field label="Account Number"><Input data-testid="op-bank-account" value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} placeholder="12345678" /></Field>
                <Field label="SWIFT / BIC"><Input data-testid="op-bank-swift" value={form.bank_swift} onChange={(e) => setForm({ ...form, bank_swift: e.target.value })} placeholder="ABCDGB2L" /></Field>
                <Field label="IBAN"><Input data-testid="op-bank-iban" value={form.bank_iban} onChange={(e) => setForm({ ...form, bank_iban: e.target.value })} placeholder="GB29 NWBK 6016 1331 9268 19" /></Field>
              </div>
            </div>
          </Card>
        </div>
      </form>
    </div>
  );
}

